import {produce} from 'immer';

import {isStaticTaskSchemaNodeData, StaticFlowchartNodeData} from '../processFlowchartConfigEditorNodeTypes';
import {FlowchartConfigWithMetadata} from '../redux/flowchartConfigAutosaveManagerCallbacks';
import {LoadedFlowchartData, StatusCountsByNodeConfigId} from './executionFlowchartData';

function applyStatusCountsToNodes(
  nodesStaticData: Array<StaticFlowchartNodeData>,
  statusCountsByNodeConfigId: StatusCountsByNodeConfigId
): Array<StaticFlowchartNodeData> {
  if (Object.keys(statusCountsByNodeConfigId).length === 0) {
    return nodesStaticData;
  }
  return produce(nodesStaticData, (draft) => {
    for (const node of draft) {
      if (!isStaticTaskSchemaNodeData(node)) {
        continue;
      }
      const counts = statusCountsByNodeConfigId[node.id];
      if (counts != null) {
        node.statusCounts = counts;
      }
    }
  });
}

/** Merges lineage-derived per-node status counts into an existing execution draft. */
export function mergeStatusCountsIntoExecutionDraft(
  draft: FlowchartConfigWithMetadata,
  statusCountsByNodeConfigId: StatusCountsByNodeConfigId
): FlowchartConfigWithMetadata {
  if (Object.keys(statusCountsByNodeConfigId).length === 0) {
    return draft;
  }
  const nodesStaticData = applyStatusCountsToNodes(
    draft.staticFlowchart.nodesStaticData,
    statusCountsByNodeConfigId
  );
  if (nodesStaticData === draft.staticFlowchart.nodesStaticData) {
    return draft;
  }
  return {
    ...draft,
    staticFlowchart: {
      ...draft.staticFlowchart,
      nodesStaticData,
    },
  };
}

/** Builds the first {@link FlowchartConfigWithMetadata} for an execution pane from resolved template data. */
export function createInitialExecutionDraftState(
  data: LoadedFlowchartData,
  flowchartTypeName: string,
  statusCountsByNodeConfigId?: StatusCountsByNodeConfigId
): FlowchartConfigWithMetadata {
  const nodesStaticData =
    statusCountsByNodeConfigId != null
      ? applyStatusCountsToNodes(data.initialStaticFlowchart.nodesStaticData, statusCountsByNodeConfigId)
      : data.initialStaticFlowchart.nodesStaticData;
  return {
    staticFlowchart: {
      ...data.initialStaticFlowchart,
      nodesStaticData,
    },
    metadata: {
      transitions: data.initialTransitions,
      rootTaskSchema: data.rootTaskSchema,
      interactionState: {
        selectedNodeIds: new Set<string>(),
        selectedRowIds: new Set<string>(),
        selectedEdgeIds: new Set<string>(),
        edgeLabelsByEdgeId: {},
      },
      currentVersionId: null,
      didInitialFlowchartHaveChainedRouters: data.hasChainedRouters,
      flowchartTypeName,
      options: {
        isFlowchartModifiableOnCreation: data.isFlowchartModifiableOnCreation,
        isFlowchartModifiableAfterCreation: data.isFlowchartModifiableAfterCreation,
      },
    },
  };
}
